import { useCallback, useRef } from "react";
import {
  useAppDispatch,
  useAppSelector,
} from "@/app/lib/features/redux/redux.hook";
import { getSimilarProducts } from "./similarProducts.thunk";
import {
  resetSimilarProducts,
  loadInitialSimilarProducts,
} from "./similarProducts.slice";
import { isLoading, isSuccess, isError } from "@/app/utils/asyncStatus";
import {
  SimilarProductsData,
  SimilarProductsRequest,
} from "./similarProducts.types";
import { FilterRequest } from "../filters/filters.types";

const useSimilarProducts = () => {
  const dispatch = useAppDispatch();
  const { data, status, error, currentSlug, ssrHydrated } = useAppSelector(
    (store) => store.similarProducts,
  );

  const lastFiltersRef = useRef<FilterRequest | undefined>(undefined);

  const fetchSimilarProducts = useCallback(
    async (slug: string, params: SimilarProductsRequest) => {
      const { page: _p, per_page: _pp, ...filters } = params;
      lastFiltersRef.current = filters as FilterRequest;
      const result = await dispatch(getSimilarProducts({ slug, params }));
      return result.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  const loadMore = useCallback(() => {
    if (!data?.pagination || !currentSlug) return;
    if (data.pagination.current_page >= data.pagination.last_page) return;

    dispatch(
      getSimilarProducts({
        slug: currentSlug,
        params: {
          ...(lastFiltersRef.current ?? {
            section_id: -1,
            seque: "PRODUCTSEQUE",
          }),
          page: data.pagination.current_page + 1,
          per_page: data.pagination.per_page,
        },
      }),
    );
  }, [dispatch, data, currentSlug]);

  /**
   * Reset similar products
   */
  const clearSimilarProducts = useCallback(() => {
    dispatch(resetSimilarProducts());
  }, [dispatch]);

  const seedSimilarProducts = useCallback(
    (data: SimilarProductsData, slug: string) =>
      dispatch(loadInitialSimilarProducts({ data, slug })),
    [dispatch],
  );

  const hasMore = data?.pagination
    ? data.pagination.current_page < data.pagination.last_page
    : false;

  return {
    // Data
    products: data?.items || [],
    pagination: data?.pagination || null,
    title: data?.title || null,
    currentSlug,
    ssrHydrated,
    hasMore,

    // Status
    status,
    error,

    // Status helpers
    isLoading: isLoading(status),
    isSuccess: isSuccess(status),
    isError: isError(status),

    // Actions
    fetchSimilarProducts,
    loadMore,
    clearSimilarProducts,
    seedSimilarProducts,
  };
};

export default useSimilarProducts;
